import { useEffect, useRef, useState } from "react";
import { IconLink, IconPlus } from "@tabler/icons-react";
import s from "../partnerPortal.module.css";
import { Panel, SectionTitle, Pill, Modal, ModalClose } from "./shared";
import { copyText, downloadCsv } from "../portalData";
import { errorHandler } from "../../../utils";
import { DEFAULT_ERROR_MESSAGE } from "../../../constants";
import {
  partnerGetSessions,
  partnerGetSession,
  partnerCreateSession,
  partnerCreateEnrollmentLink,
  partnerListEnrollmentLinks,
  partnerGetTests,
  rolesFromTestsResponse,
  workingAssessmentUrl,
} from "../../../services/partnerCalls";
import PartnerRoleOptions from "../PartnerRoleOptions";
import { usePartnerContext } from "../../../context/partnerContext";
import ResultReport from "./ResultReport";
import { Spinner } from "../../../components/UI/Spinner/Spinner";
import { buildReport } from "./reportData";
import { PARTNER_ROLE_CATALOG, mergePartnerRoleCatalog, displayRole } from "../partnerRoleCatalog";
import { parseBulkCandidates } from "../parseBulkCandidates";

// A result report is final once it's scored (or parked for review) and, for a
// scenario test, its interview questions are settled. Until then the modal
// refreshes itself every RESULT_POLL_MS, up to RESULT_POLL_MAX times.
const RESULT_POLL_MS = 3000;
const RESULT_POLL_MAX = 10;
const isFinalReport = (rep) =>
  rep.kind !== "sjt" ||
  (rep.status !== "pending" && !(rep.status === "scored" && rep.competencies?.length && !["ready", "none", "failed"].includes(rep.probesStatus)));

const COLS = "1.2fr 1.3fr 1.4fr 0.6fr 1fr 0.9fr 0.8fr";
const PAGE_SIZE = 20;
const emptyCounts = { all: 0, pending: 0, in_progress: 0, completed: 0, expired: 0 };

const fmtDate = (iso) => {
  if (!iso) return "just now";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const LINK_COLS = "1.4fr 0.7fr 0.9fr 0.8fr 0.8fr";

const LINK_STATE = {
  expired: { label: "expired", pill: "pillExpired" },
  full: { label: "full", pill: "pillExpired" },
  disabled: { label: "closed", pill: "pillExpired" },
};

const fmtDay = (iso) => {
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

const mapRow = (sn) => {
  const test = sn.test_id && typeof sn.test_id === "object" ? sn.test_id : null;
  const rubric = sn.rubric_test_id && typeof sn.rubric_test_id === "object" ? sn.rubric_test_id : null;
  return {
    id: sn.session_id,
    cand:
      sn.candidate_name ||
      (sn.candidate_email || "").split("@")[0] ||
      "Candidate",
    role: displayRole(test?.role || rubric?.role || sn.test_role || ""),
    cr: sn.is_sandbox ? 0 : (test?.credit_cost ?? rubric?.creditCost ?? sn.credit_cost ?? 0),
    status: sn.status,
    timedOut: !!sn.timed_out,
    date: fmtDate(sn.createdAt || sn.created_date),
  };
};

export default function Sessions() {
  const [mode, setMode] = useState("single");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [roles, setRoles] = useState(PARTNER_ROLE_CATALOG);
  const [role, setRole] = useState("");
  const [bulk, setBulk] = useState("");
  const [flash, setFlash] = useState(null);
  const [delivery, setDelivery] = useState("email");
  const [bulkDelivery, setBulkDelivery] = useState("email");
  const [cap, setCap] = useState(100);
  const [expiry, setExpiry] = useState(30);
  const [linkModal, setLinkModal] = useState(null);
  const [copied, setCopied] = useState(false);
  const [resultModal, setResultModal] = useState(null);
  // shown the instant "View" is clicked, until the report arrives
  const [resultLoading, setResultLoading] = useState(null);
  const resultCache = useRef(new Map()); // session id → final report
  const resultInflight = useRef(new Map()); // session id → pending request
  const openResultId = useRef(null);
  const resultPolls = useRef(0);
  const [pdfBusy, setPdfBusy] = useState(false);
  const { profileData } = usePartnerContext();
  const brandName = profileData?.branding?.brandName || "";
  const [list, setList] = useState([]);
  const [links, setLinks] = useState([]);
  const [copiedLink, setCopiedLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState("newest");
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState(emptyCounts);

  const optionsForMode = roles;
  const selected =
    optionsForMode.find((r) => r.t === role) || optionsForMode[0] || { t: "", c: 0 };
  const roleObj = selected.t ? selected : { t: role, c: 0 };

  const fetchRoles = async () => {
    try {
      const res = await partnerGetTests();
      const list = mergePartnerRoleCatalog(rolesFromTestsResponse(res));
      setRoles(list);
      if (list.length) setRole((prev) => prev || list[0].t);
    } catch (error) {
      if (error?.response?.data?.message) {
        errorHandler(error.response.data.message || DEFAULT_ERROR_MESSAGE);
      }
    }
  };

  const doCopy = (text) => {
    copyText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const fetchLinks = async () => {
    try {
      setLinks(await partnerListEnrollmentLinks());
    } catch (error) {
      if (error?.response?.data?.message) errorHandler(error.response.data.message);
    }
  };

  const copySharedLink = (link) => {
    copyText(workingAssessmentUrl(link.url));
    setCopiedLink(link.id);
    setTimeout(() => setCopiedLink((id) => (id === link.id ? null : id)), 1800);
  };

  const parseBulk = () => parseBulkCandidates(bulk, roles, roleObj);

  const fetchSessions = async () => {
    try {
      const res = await partnerGetSessions({
        status: statusFilter || undefined,
        role: roleFilter || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo ? `${dateTo}T23:59:59.999Z` : undefined,
        sort,
        limit: PAGE_SIZE,
        offset,
      });
      const data = res?.data?.data;
      const rows = data?.sessions;
      if (Array.isArray(rows)) setList(rows.map(mapRow));
      if (data?.pagination) setTotal(data.pagination.total || 0);
      if (data?.counts) setCounts({ ...emptyCounts, ...data.counts });
    } catch (error) {
      if (error?.response?.data?.message) {
        errorHandler(error.response.data.message || DEFAULT_ERROR_MESSAGE);
      }
    }
  };

  const assignOne = async () => {
    if (!email.trim() || !roleObj.t || busy) return;
    const cand = name.trim() || email.split("@")[0];
    setBusy(true);
    try {
      const sn = await partnerCreateSession({
        candidate_email: email.trim(),
        candidate_name: name.trim() || undefined,
        role: roleObj.t,
      });
      const link = workingAssessmentUrl(sn?.candidate_url || sn?.test_url);
      const cr = roleObj.c;
      if (delivery === "link") {
        setLinkModal({ cand, email: email.trim(), role: roleObj.t, cr, link });
        setCopied(false);
        setFlash(null);
      } else {
        setFlash({ type: "single", email: email.trim(), role: roleObj.t, cr });
      }
      setName("");
      setEmail("");
      fetchSessions();
    } catch (error) {
      errorHandler(error?.response?.data?.message || DEFAULT_ERROR_MESSAGE);
    } finally {
      setBusy(false);
    }
  };

  const assignBulk = async () => {
    const { items, issues } = parseBulk();
    if (issues.length) {
      setFlash({ type: "bulkparse", issues });
      return;
    }
    if (!items.length || !roleObj.t || busy) return;
    setBusy(true);
    try {
      // One row failing (say, that candidate already has this test open)
      // must not stop the rest of the list.
      const results = [];
      const failed = [];
      let totalCr = 0;
      for (const it of items) {
        try {
          const sn = await partnerCreateSession({
            candidate_email: it.email,
            role: it.role,
          });
          results.push({ email: it.email, role: it.role, link: workingAssessmentUrl(sn?.candidate_url || sn?.test_url) });
          totalCr += it.cr || 0;
        } catch (error) {
          failed.push({ email: it.email, role: it.role, reason: error?.response?.data?.message || DEFAULT_ERROR_MESSAGE });
        }
      }
      if (bulkDelivery === "download" && results.length) {
        const csv =
          "email,role,test_link\n" +
          results
            .map((r) => r.email + "," + r.role + "," + (r.link || ""))
            .join("\n");
        downloadCsv("awasource-test-links.csv", csv);
      }
      setFlash({
        type: bulkDelivery === "download" ? "bulkdl" : "bulk",
        count: results.length,
        cr: totalCr,
        failed,
      });
      setBulk(failed.map((f) => `${f.email}, ${f.role}`).join("\n"));
      fetchSessions();
    } finally {
      setBusy(false);
    }
  };

  const assignShared = async () => {
    if (!roleObj.t || busy) return;
    setBusy(true);
    try {
      const out = await partnerCreateEnrollmentLink({
        role: roleObj.t,
        cap,
        expiryDays: expiry,
      });
      setLinkModal({
        shared: true,
        role: roleObj.t,
        cr: roleObj.c,
        cap,
        expiry,
        link: workingAssessmentUrl(out?.url),
      });
      setCopied(false);
      setFlash(null);
      fetchLinks();
    } catch (error) {
      errorHandler(error?.response?.data?.message || DEFAULT_ERROR_MESSAGE);
    } finally {
      setBusy(false);
    }
  };

  // One request per session at a time; finished reports are kept, so
  // reopening (or a prefetch on hover) makes the modal instant.
  const fetchReport = (id) => {
    if (resultCache.current.has(id)) return Promise.resolve(resultCache.current.get(id));
    if (resultInflight.current.has(id)) return resultInflight.current.get(id);
    const req = partnerGetSession(id)
      .then((d) => {
        const rep = { ...buildReport(d), _id: id };
        if (isFinalReport(rep)) resultCache.current.set(id, rep);
        return rep;
      })
      .finally(() => resultInflight.current.delete(id));
    resultInflight.current.set(id, req);
    return req;
  };

  const prefetchResult = (id) => {
    fetchReport(id).catch(() => {});
  };

  const closeResult = () => {
    openResultId.current = null;
    setResultModal(null);
    setResultLoading(null);
  };

  const openResult = async (session) => {
    const id = session.id;
    openResultId.current = id;
    resultPolls.current = 0;
    const cached = resultCache.current.get(id);
    if (cached) {
      setResultModal(cached);
      return;
    }
    setResultModal(null);
    setResultLoading({ cand: session.cand, role: session.role });
    try {
      const rep = await fetchReport(id);
      if (openResultId.current === id) setResultModal(rep);
    } catch (error) {
      if (openResultId.current === id) {
        closeResult();
        errorHandler(error?.response?.data?.message || DEFAULT_ERROR_MESSAGE);
      }
    } finally {
      if (openResultId.current === id) setResultLoading(null);
    }
  };

  // Scoring still running, or interview questions still being written:
  // refresh the open report in place instead of asking to reopen it.
  useEffect(() => {
    if (!resultModal || isFinalReport(resultModal) || resultPolls.current >= RESULT_POLL_MAX) return undefined;
    const id = resultModal._id;
    const t = setTimeout(async () => {
      resultPolls.current += 1;
      try {
        const rep = await fetchReport(id);
        if (openResultId.current === id) setResultModal(rep);
      } catch {
        /* keep showing what we have */
      }
    }, RESULT_POLL_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultModal]);

  const downloadReport = async () => {
    if (!resultModal) return;
    setPdfBusy(true);
    try {
      // jsPDF is only loaded when someone actually downloads a report.
      const { downloadResultPdf } = await import("./reportPdf");
      downloadResultPdf(resultModal, { brandName });
    } catch (error) {
      errorHandler("Couldn't create the PDF. Please try again.");
    } finally {
      setPdfBusy(false);
    }
  };

  const resetPage = (fn) => (value) => {
    fn(value);
    setOffset(0);
  };

  const toggleSort = (key) => {
    if (key === "assigned") setSort((prev) => (prev === "newest" ? "oldest" : "newest"));
    else setSort(key);
    setOffset(0);
  };

  useEffect(() => {
    fetchRoles();
    fetchLinks();
  }, []);

  useEffect(() => {
    fetchSessions();
    // Filters and page are the inputs; fetchSessions closes over them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, roleFilter, dateFrom, dateTo, sort, offset]);

  const primaryLabel =
    mode === "single"
      ? delivery === "link"
        ? "Generate link →"
        : "Send test →"
      : mode === "bulk"
        ? bulkDelivery === "download"
          ? "Download links →"
          : "Send tests →"
        : "Generate shared link →";

  return (
    <div>
      <SectionTitle
        title="Test Sessions"
        sub="Assign psychometric tests to your candidates and track results"
      />

      {/* Assign panel */}
      <Panel className={s.pg66}>
        <div className={s.pg67}
        >
          <div className={s.pg68}
          >
            <IconPlus size={15} /> Assign a test
          </div>
          <div className={s.segment}>
            {[
              ["single", "Single"],
              ["bulk", "Bulk / CSV"],
              ["shared", "Shared link"],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`${s.segmentBtn} ${mode === id ? s.segmentBtnActive : ""}`}
                onClick={() => {
                  setMode(id);
                  setFlash(null);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {mode === "single" && (
          <div className={s.pg69}
          >
            <div>
              <label className={s.label}>Candidate name</label>
              <input
                className={s.input}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div>
              <label className={s.label}>Candidate email *</label>
              <input
                className={s.input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="candidate@email.com"
              />
            </div>
            <div>
              <label className={s.label}>Role / test</label>
              <select
                className={s.select}
                value={roleObj.t}
                onChange={(e) => setRole(e.target.value)}
              >
                <PartnerRoleOptions roles={optionsForMode} includeCredits />
              </select>
            </div>
          </div>
        )}

        {mode === "bulk" && (
          <div className={s.pg70}
          >
            <div className={s.pg71}>
              <label className={s.label}>
                Candidate list — one per line: email, or email, role
              </label>
              <textarea
                className={`${s.textarea} ${s.pg72}`}
                value={bulk}
                onChange={(e) => setBulk(e.target.value)}
                placeholder={
                  "a.bello@itm.com\nc.okoro@itm.com, Data Scientist\nt.eze@itm.com, Backend Developer"
                }
              />
              <p className={s.bulkHint}>
                Use the role names from the dropdown — spaces and hyphens are
                fine (<b>Front-end Developer</b> is Frontend Developer). A
                typed role that is not in the list is left unsent; it will not
                take the default.
              </p>
            </div>
            <div>
              <label className={s.label}>
                Default role (rows with no role)
              </label>
              <select
                className={s.select}
                value={roleObj.t}
                onChange={(e) => setRole(e.target.value)}
              >
                <PartnerRoleOptions roles={optionsForMode} includeCredits />
              </select>
            </div>
          </div>
        )}

        {mode === "shared" && (
          <div className={s.pg73}
          >
            <div>
              <label className={s.label}>Role / test</label>
              <select
                className={s.select}
                value={roleObj.t}
                onChange={(e) => setRole(e.target.value)}
              >
                <PartnerRoleOptions roles={optionsForMode} includeCredits />
              </select>
            </div>
            <div>
              <label className={s.label}>Max tests (cap)</label>
              <input
                type="number"
                min="1"
                className={s.input}
                value={cap}
                onChange={(e) =>
                  setCap(Math.max(1, parseInt(e.target.value, 10) || 0))
                }
              />
            </div>
            <div>
              <label className={s.label}>Expires in (days)</label>
              <input
                type="number"
                min="1"
                className={s.input}
                value={expiry}
                onChange={(e) =>
                  setExpiry(Math.max(1, parseInt(e.target.value, 10) || 0))
                }
              />
            </div>
          </div>
        )}

        <div className={s.pg74}
        >
          <div className={s.pg75}
          >
            {mode === "single" && (
              <>
                This role uses <b className={s.pg24}>{roleObj.c} credits</b>,
                deducted when the candidate starts the test. Sending the
                invite does not charge you. Emails carry your logo (set in
                Settings).
              </>
            )}
            {mode === "bulk" && (
              <>
                Each role's credits are deducted when that candidate starts,
                not when you send the invite. Email everyone, or{" "}
                <b className={s.pg24}>download a CSV of links</b> to
                send from your own system.
              </>
            )}
            {mode === "shared" && (
              <>
                One reusable link for <b className={s.pg24}>{roleObj.t || "the selected role"}</b>{" "}
                — post it anywhere. Candidates enter their details to start;{" "}
                <b>{roleObj.c} credits are used when a candidate starts</b> (one per
                person), capped at {cap} and expiring in {expiry} days.
              </>
            )}
          </div>

          <div className={s.pg76}
          >
            {mode === "single" && (
              <div className={s.segment}>
                {[
                  ["email", "Email"],
                  ["link", "Copy link"],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    className={`${s.segmentBtn} ${delivery === id ? s.segmentBtnAltActive : ""}`}
                    onClick={() => {
                      setDelivery(id);
                      setFlash(null);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            {mode === "bulk" && (
              <div className={s.segment}>
                {[
                  ["email", "Email all"],
                  ["download", "Download links"],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    className={`${s.segmentBtn} ${bulkDelivery === id ? s.segmentBtnAltActive : ""}`}
                    onClick={() => {
                      setBulkDelivery(id);
                      setFlash(null);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              className={`${s.btnSolid} ${s.pg77}`}
              disabled={!roleObj.t || busy}
              onClick={
                mode === "single"
                  ? assignOne
                  : mode === "bulk"
                    ? assignBulk
                    : assignShared
              }
            >
              {primaryLabel}
            </button>
          </div>
        </div>

        {flash?.type === "bulkparse" && (
          <div className={s.flashWarn}>
            Fix these rows before sending — nothing was emailed:
            <ul className={s.flashIssueList}>
              {flash.issues.map((issue) => (
                <li key={`${issue.line}-${issue.reason}`}>
                  Line {issue.line}: {issue.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
        {flash && flash.type !== "bulkparse" && (
          <div className={s.flashOk}>
            {flash.type === "single" && (
              <div>
                <div className={s.flashOkText}>
                  ✓ {displayRole(flash.role)} test emailed to {flash.email} · {flash.cr} credits
                  when they start
                </div>
                <div className={s.flashSub}>
                  Sent from your branded email. Results arrive by webhook when
                  complete.
                </div>
              </div>
            )}
            {flash.type === "bulk" && flash.count > 0 && (
              <div className={s.flashOkText}>
                ✓ {flash.count} tests emailed · up to {flash.cr} credits, used
                as each candidate starts.
              </div>
            )}
            {flash.type === "bulkdl" && flash.count > 0 && (
              <div>
                <div className={s.flashOkText}>
                  ✓ {flash.count} links generated · up to {flash.cr} credits,
                  used as each candidate starts · downloaded as CSV
                </div>
                <div className={s.flashSub}>
                  File: awasource-test-links.csv (email, role, test_link) —
                  mail-merge it through your own system.
                </div>
              </div>
            )}
            {flash.failed?.length > 0 && (
              <div className={s.flashSub}>
                {flash.failed.length} not sent (left in the list above to retry):{" "}
                {flash.failed.map((f) => `${f.email} (${displayRole(f.role)}) — ${f.reason}`).join("; ")}
              </div>
            )}
          </div>
        )}
      </Panel>

      {mode === "shared" && (
        <Panel flush>
          <div className={s.pg36}>
            <div className={s.pg78}>
              <div className={s.tableHead} style={{ gridTemplateColumns: LINK_COLS }}>
                {["Your shared links", "Used", "Expires", "Status", "Link"].map((h) => (
                  <div key={h} className={s.th}>
                    {h}
                  </div>
                ))}
              </div>
              {links.length === 0 && (
                <div className={s.tableRow} style={{ gridTemplateColumns: "1fr" }}>
                  <div className={`${s.td} ${s.dash}`}>No shared links yet.</div>
                </div>
              )}
              {links.map((link) => {
                const state = LINK_STATE[link.closed_reason] || { label: "active", pill: "pillCompleted" };
                return (
                  <div key={link.id} className={s.tableRow} style={{ gridTemplateColumns: LINK_COLS }}>
                    <div className={`${s.td} ${s.tdStrong}`}>{displayRole(link.role)}</div>
                    <div className={s.td}>
                      {link.used} / {link.cap}
                    </div>
                    <div className={s.td}>{fmtDay(link.expires_at)}</div>
                    <div className={s.td}>
                      <span className={`${s.pill} ${s[state.pill]}`}>{state.label}</span>
                    </div>
                    <div className={s.td}>
                      <button type="button" className={s.linkTeal} onClick={() => copySharedLink(link)}>
                        {copiedLink === link.id ? "Copied ✓" : "Copy link"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Panel>
      )}

      {/* Sessions table */}
      <Panel flush>
        <div className={s.tableToolbar}>
          <div className={s.tableToolbarField}>
            <label className={s.label} htmlFor="session-status">Status</label>
            <select
              id="session-status"
              className={s.select}
              value={statusFilter}
              onChange={(e) => resetPage(setStatusFilter)(e.target.value)}
            >
              <option value="">All statuses ({counts.all})</option>
              <option value="pending">Pending ({counts.pending || 0})</option>
              <option value="in_progress">In progress ({counts.in_progress || 0})</option>
              <option value="completed">Completed ({counts.completed || 0})</option>
              <option value="expired">Expired ({counts.expired || 0})</option>
            </select>
          </div>
          <div className={s.tableToolbarField}>
            <label className={s.label} htmlFor="session-role">Role</label>
            <select
              id="session-role"
              className={s.select}
              value={roleFilter}
              onChange={(e) => resetPage(setRoleFilter)(e.target.value)}
            >
              <option value="">All roles</option>
              <PartnerRoleOptions roles={optionsForMode} />
            </select>
          </div>
          <div className={s.tableToolbarFieldNarrow}>
            <label className={s.label} htmlFor="session-from">From</label>
            <input
              id="session-from"
              type="date"
              className={s.input}
              value={dateFrom}
              onChange={(e) => resetPage(setDateFrom)(e.target.value)}
            />
          </div>
          <div className={s.tableToolbarFieldNarrow}>
            <label className={s.label} htmlFor="session-to">To</label>
            <input
              id="session-to"
              type="date"
              className={s.input}
              value={dateTo}
              onChange={(e) => resetPage(setDateTo)(e.target.value)}
            />
          </div>
          <div className={s.tableToolbarFieldNarrow}>
            <label className={s.label} htmlFor="session-sort">Sort</label>
            <select
              id="session-sort"
              className={s.select}
              value={sort}
              onChange={(e) => resetPage(setSort)(e.target.value)}
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="role">Role A–Z</option>
              <option value="status">Status</option>
            </select>
          </div>
        </div>
        <div className={s.pg36}>
          <div className={s.pg78}>
            <div className={s.tableHead} style={{ gridTemplateColumns: COLS }}>
              <div className={s.th}>Session</div>
              <div className={s.th}>Candidate</div>
              <button
                type="button"
                className={`${s.thBtn} ${sort === "role" ? s.thBtnActive : ""}`}
                onClick={() => toggleSort("role")}
              >
                Role
              </button>
              <div className={s.th}>Credits</div>
              <button
                type="button"
                className={`${s.thBtn} ${sort === "status" ? s.thBtnActive : ""}`}
                onClick={() => toggleSort("status")}
              >
                Status
              </button>
              <button
                type="button"
                className={`${s.thBtn} ${sort === "newest" || sort === "oldest" ? s.thBtnActive : ""}`}
                onClick={() => toggleSort("assigned")}
              >
                Assigned
              </button>
              <div className={s.th}>Result</div>
            </div>

            {list.length === 0 && (
              <div className={s.tableRow} style={{ gridTemplateColumns: "1fr" }}>
                <div className={`${s.td} ${s.dash}`}>
                  No sessions match these filters.
                </div>
              </div>
            )}

            {list?.map((session, i) => (
              <div
                key={session.id + i}
                className={s.tableRow}
                style={{ gridTemplateColumns: COLS }}
              >
                <div className={`${s.td} ${s.tdMono}`}>{session.id}</div>
                <div className={`${s.td} ${s.tdStrong}`}>{session.cand}</div>
                <div className={s.td}>{displayRole(session.role)}</div>
                <div className={`${s.td} ${s.pg14}`}>
                  {session.cr}C
                </div>
                <div className={s.td}>
                  <Pill status={session.status} />
                </div>
                <div
                  className={`${s.td} ${s.pg79}`}
                >
                  {session.date}
                </div>
                <div className={s.td}>
                  {session.status === "completed" ? (
                    <button
                      type="button"
                      className={s.linkTeal}
                      onMouseEnter={() => prefetchResult(session.id)}
                      onFocus={() => prefetchResult(session.id)}
                      onTouchStart={() => prefetchResult(session.id)}
                      onClick={() => openResult(session)}
                    >
                      View →
                    </button>
                  ) : (
                    <span className={s.dash}>—</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={s.tablePager}>
          <div>
            {total
              ? `Showing ${offset + 1}–${Math.min(offset + list.length, total)} of ${total}`
              : "No sessions"}
          </div>
          <div className={s.tablePagerBtns}>
            <button
              type="button"
              className={s.btnOutline}
              disabled={offset === 0}
              onClick={() => setOffset((n) => Math.max(0, n - PAGE_SIZE))}
            >
              Previous
            </button>
            <button
              type="button"
              className={s.btnOutline}
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset((n) => n + PAGE_SIZE)}
            >
              Next
            </button>
          </div>
        </div>
      </Panel>

      {/* Link-ready modal */}
      <Modal open={!!linkModal} onClose={() => setLinkModal(null)}>
        {linkModal && (
          <>
            <div className={`${s.modalHead} ${s.modalHeadCentered}`}>
              <ModalClose onClose={() => setLinkModal(null)} />
              <div className={s.modalIcon}>
                <IconLink size={21} />
              </div>
              <div className={`${s.modalTitle} ${s.modalTitleLg}`}>
                {linkModal.shared
                  ? "Shared test link ready"
                  : "Test link ready"}
              </div>
              <div className={s.modalSub}>
                {linkModal.shared
                  ? `${displayRole(linkModal.role)} · self-serve pool`
                  : `${displayRole(linkModal.role)} · ${linkModal.cr} credits when the candidate starts`}
              </div>
            </div>

            <div className={s.modalBody}>
              {linkModal.shared ? (
                <div className={s.statRow}>
                  {[
                    ["Max tests", linkModal.cap],
                    ["Expires", `${linkModal.expiry} days`],
                    ["Credits / test", `${linkModal.cr}C`],
                  ].map(([k, v]) => (
                    <div key={k} className={s.statCell}>
                      <div className={s.statCellLabel}>{k}</div>
                      <div className={s.statCellValue}>{v}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className={s.metaLabel}>Candidate</div>
                  <div className={s.pg80}
                  >
                    {linkModal.cand}{" "}
                    <span className={s.pg81}>
                      · {linkModal.email}
                    </span>
                  </div>
                </>
              )}

              <div className={s.metaLabel}>Test link</div>
              <div className={s.copyRow}>
                <input
                  readOnly
                  className={`${s.copyInput} ${copied ? s.copyInputDone : ""}`}
                  value={linkModal.link}
                  onFocus={(e) => e.target.select()}
                />
                <button
                  type="button"
                  className={`${s.copyBtn} ${copied ? s.copyBtnDone : ""}`}
                  onClick={() => doCopy(linkModal.link)}
                >
                  {copied ? "Copied ✓" : "Copy link"}
                </button>
              </div>

              <div className={s.chipRow}>
                {["WhatsApp", "Email", "Your ATS", "Any channel"].map(
                  (label) => (
                    <span key={label} className={s.chip}>
                      {label}
                    </span>
                  ),
                )}
              </div>

              <div className={`${s.infoBox} ${s.pg6}`}>
                <div className={s.infoBoxText}>
                  {linkModal.shared
                    ? `Post this one link anywhere — job ad, WhatsApp, your careers page. Each candidate enters their details and takes the test; you get a result per person by webhook. ${linkModal.cr} credits are used when each candidate starts. It stops at the cap, the expiry, or when your balance runs out. You can copy it again later from "Your shared links" below.`
                    : `Paste it wherever you like. The candidate takes the test on your white-label page, and the result lands here and on your webhook when they finish. ${linkModal.cr} credits are used when they start.`}
                </div>
              </div>

              <div className={s.pg82}>
                <button
                  type="button"
                  className={s.btnOutline}
                  onClick={() => setLinkModal(null)}
                >
                  Done
                </button>
                <button
                  type="button"
                  className={s.btnMuted}
                  onClick={() => setLinkModal(null)}
                >
                  Assign another
                </button>
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* Result modal — opens at once; the report fills in when it arrives */}
      <Modal open={!!resultModal || !!resultLoading} onClose={closeResult} maxWidth={680}>
        {resultModal ? (
          <ResultReport
            report={resultModal}
            brandName={brandName}
            onClose={closeResult}
            onDownload={downloadReport}
            downloading={pdfBusy}
          />
        ) : resultLoading ? (
          <>
            <div className={s.modalHead}>
              <ModalClose onClose={closeResult} />
              <div className={`${s.modalTitle} ${s.modalTitleLg}`}>{resultLoading.cand}</div>
              <div className={s.modalSub}>{[resultLoading.role, "loading the assessment report…"].filter(Boolean).join(" · ")}</div>
            </div>
            <div className={s.modalBody} style={{ padding: "28px 24px 32px" }}>
              <Spinner />
            </div>
          </>
        ) : null}
      </Modal>
    </div>
  );
}
